import React from 'react'
import { Box } from '@mui/material'
import { dsColorNew } from '@palette'
import { DateRangePickerSeeker } from '@ui-seeker/core'
import { format as formatDate } from 'date-fns'
import { pickerState, rangeFromPicker, toIso } from 'Pages/DsRepricing/@services/dateRange'

/**
 * One calendar for a `{from, to}` pair, everywhere DS Repricing asks
 * for a date range (Curro, 2026-10-02: "un calendario desplegable…
 * en todos lados con rango de fechas").
 *
 * Wraps the app's `DateRangePickerSeeker` (rsuite behind a MUI
 * FormControl — the same control Seasons already uses) so the three
 * screens that used to draw two `<input type="date">` boxes share one
 * look and one behaviour: ISO strings in and out, quick ranges on the
 * left of the calendar, 40px tall like every other small input.
 *
 * The wrapper's defaults suit a filter over history — it refuses any
 * day after today — which is the opposite of a price limit or a
 * schedule window. Every day is selectable here unless the caller says
 * otherwise.
 */
const FORMAT = 'dd MMM yyyy'
const ANY_DAY = () => false

// rsuite decides the toggle's height, not MUI (`.rs-*` classes), and
// its own emotion styles beat a plain sx block — hence `!important`.
const heightSx = {
	'& .rs-picker-toggle-wrapper, & .rs-picker-toggle, & .rs-picker-input, & .rs-input': {
		minHeight: '40px !important',
		height: '40px !important',
	},
	'& .rs-picker-toggle': {
		paddingTop: '6px !important',
		paddingBottom: '6px !important',
	},
	'& .rs-picker-toggle-value, & .rs-picker-toggle-placeholder': {
		fontSize: '14px !important',
	},
}

export default function DateRangeField({
	from, to, onChange,
	label = '',
	placeholder = 'Any dates',
	cleanable = false,
	presets = [],
	shouldDisableDate = ANY_DAY,
	width = 260,
	sx = {},
	// Both off by default, so the three screens using this do not move
	// (CU-869fhtp3a, D2). `oneTap` is rsuite's: the first click confirms
	// and closes, so a range can only come from a preset — forwarded for
	// whoever wants that, not used by Activity. `collapseSameDay` paints
	// `[d, d]` as `31 Dec 2026` instead of `31 Dec 2026 → 31 Dec 2026`:
	// a day picked as click, click, OK reads as a day.
	oneTap = false,
	collapseSameDay = false,
}) {
	const fmt = (d) => formatDate(d, FORMAT)
	const { value, placeholder: openEnd } = pickerState(from, to, fmt)
	const renderValue = collapseSameDay
		? ([a, b]) => (a && b && toIso(a) === toIso(b) ? fmt(a) : `${fmt(a)} → ${fmt(b)}`)
		: undefined

	return (
		<Box sx={{ width, flex: 'none', bgcolor: dsColorNew.white, borderRadius: 1, ...heightSx, ...sx }}>
			<DateRangePickerSeeker
				label={label}
				value={value}
				placeholder={openEnd || placeholder}
				onChange={(v) => onChange(rangeFromPicker(v))}
				format={FORMAT}
				character=" ~ "
				cleanable={cleanable}
				ranges={presets}
				placement="bottomStart"
				showWeekNumbers={false}
				shouldDisableDate={shouldDisableDate}
				oneTap={oneTap}
				renderValue={renderValue}
				block/>
		</Box>
	)
}
